import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getReplaceItemRepor,
  getComplementaryReportApi,
  getSmsUsageHistoryApi,
} from "@/api/settings/cost-management";

const fallbackSmsUsageHistory = [
  {
    month: "October 2026",
    total_sms_send: 2000,
    per_sms_charge: 0.0011,
    total_charge: 2.2,
  },
  {
    month: "September 2026",
    total_sms_send: 1900,
    per_sms_charge: 0.0011,
    total_charge: 2.09,
  },
  {
    month: "August 2026",
    total_sms_send: 1800,
    per_sms_charge: 0.0011,
    total_charge: 1.98,
  },
  {
    month: "July 2026",
    total_sms_send: 1700,
    per_sms_charge: 0.0011,
    total_charge: 1.87,
  },
  {
    month: "June 2026",
    total_sms_send: 1600,
    per_sms_charge: 0.0011,
    total_charge: 1.76,
  },
];

// Thunks
export const fetchReplaceItemReport = createAsyncThunk(
  "costManagement/fetchReplaceItemReport",
  async ({ restaurant_id, params }, { rejectWithValue }) => {
    const res = await getReplaceItemRepor(restaurant_id, params);
    if (res.status === "success") return res.data;
    return rejectWithValue(res.message);
  },
);

export const fetchComplementaryReport = createAsyncThunk(
  "costManagement/fetchComplementaryReport",
  async ({ restaurant_id, params }, { rejectWithValue }) => {
    const res = await getComplementaryReportApi(restaurant_id, params);
    if (res.status === "success") return res.data;
    return rejectWithValue(res.message);
  },
);

export const fetchSmsUsageHistory = createAsyncThunk(
  "costManagement/fetchSmsUsageHistory",
  async ({ restaurant_id, params }, { rejectWithValue }) => {
    try {
      const res = await getSmsUsageHistoryApi(restaurant_id, params);
      if (res && res.status === "success" && res.data) {
        return (
          res.data.sms_usage_history ||
          (Array.isArray(res.data) ? res.data : fallbackSmsUsageHistory)
        );
      }
      // If endpoint returns without success format, use fallback
      return fallbackSmsUsageHistory;
    } catch {
      // In case endpoint is 404/not yet deployed on live server, use fallback
      return fallbackSmsUsageHistory;
    }
  },
);

// Initial State
const initialState = {
  orders: [],
  smsUsageHistory: [],
  smsLoading: false,
  smsError: null,
  pagination: null,
  statistics: null,
  getLoading: false,
  error: null,
};

// Slice
const costManagementSlice = createSlice({
  name: "costManagement",
  initialState,
  reducers: {
    clearCostManagementErrors: (state) => {
      state.error = null;
      state.smsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReplaceItemReport.pending, (state) => {
        state.getLoading = true;
        state.error = null;
      })
      .addCase(fetchReplaceItemReport.fulfilled, (state, action) => {
        state.getLoading = false;
        state.orders = action.payload.orders;
        state.pagination = action.payload.pagination;
        state.statistics = action.payload.statistics;
      })
      .addCase(fetchReplaceItemReport.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchComplementaryReport.pending, (state) => {
        state.getLoading = true;
        state.error = null;
      })
      .addCase(fetchComplementaryReport.fulfilled, (state, action) => {
        state.getLoading = false;
        state.orders = action.payload.orders;
        state.pagination = action.payload.pagination;
        state.statistics = action.payload.statistics;
      })
      .addCase(fetchComplementaryReport.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchSmsUsageHistory.pending, (state) => {
        state.smsLoading = true;
        state.smsError = null;
      })
      .addCase(fetchSmsUsageHistory.fulfilled, (state, action) => {
        state.smsLoading = false;
        state.smsUsageHistory = action.payload;
      })
      .addCase(fetchSmsUsageHistory.rejected, (state, action) => {
        state.smsLoading = false;
        state.smsError = action.payload;
      });
  },
});

export const { clearCostManagementErrors } = costManagementSlice.actions;
export default costManagementSlice.reducer;
